<template>
  <div class="flex items-center justify-center p-2">
    <!-- Dropzone -->
    <div v-if="pages.length === 0" 
      @click="fileInput.click()"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
      class="w-full max-w-4xl bg-gradient-to-b from-red-50 to-white hover:from-white hover:to-red-50 mt-5 transition-all duration-300 cursor-pointer shadow-lg rounded-2xl flex flex-col items-center justify-center text-center py-10 px-8 relative overflow-hidden border-2 border-dashed"
      :class="[isDragging ? 'border-red-500 bg-red-50 shadow-2xl' : 'border-red-400']">
      
      <!-- Loading indicator for file processing -->
      <div v-if="isProcessing" class="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center z-50 rounded-2xl">
        <div class="animate-spin rounded-full h-16 w-16 border-4 border-red-200 border-t-red-600 mb-4"></div>
        <p class="text-gray-700 font-semibold text-lg">{{ t('processing_pages', { count: totalPagesBeingProcessed }) }}</p>
        <p class="text-gray-500 text-sm mt-2">{{ t('take_moment') }}</p>
      </div>

      <div class="relative z-10 flex flex-col items-center">
        <div class="mx-auto w-20 h-20 flex items-center justify-center mb-4">
          <img src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/pdf%20%282%29-g2dPLzFcgQFJYtGGxU0T9PMNB8UPUx.png" alt="PDF Upload" class="w-full h-full object-contain" />
        </div>
        
        <!-- Upload Button -->
        <button
          @click.stop="fileInput.click()"
          class="bg-red-500 text-white font-semibold px-6 py-2 rounded-lg  active:bg-red-600 transition-all duration-200 flex items-center gap-2 shadow-md">
          <Upload :size="20" />
          <span class="font-semibold">{{ t('choose_files') }}</span>
        </button>
        
        <p class="mb-5 font-semibold text-base text-gray-700 mt-6">
          {{ t('drag_drop') }}
        </p>
        <p class="text-sm text-gray-600 font-medium">
          {{ t('or_press') }} <span class="bg-gray-200 px-2 py-1 rounded font-mono text-gray-800">CTRL + V</span> {{ t('to_paste') }}
        </p>
      </div>
    </div>
    <!-- Main Editor -->
    <div v-else class="bg-white rounded-lg w-full max-w-6xl shadow-md border-t-4 border-red-500 mt-3">
      <div class="max-w-6xl mx-auto">
        <!-- Toolbar -->
        <div class="bg-white rounded-sm p-4 border-b border-red-100">
          <div class="flex flex-wrap gap-3">
              <p class="text-gray-800 text-xl ml-3 mt-1 flex items-center flex-wrap gap-1">
                <span v-if="fileInfo.uniqueFiles.length" class="flex items-center gap-1 flex-wrap">
                  {{ t('editing') }}
                  <template v-if="fileInfo.fileCount <= 3">
                    <template v-for="(fileName, index) in fileInfo.uniqueFiles" :key="fileName">
                      <span class="font-bold truncate max-w-[150px]" :style="{ color: fileColors[fileName] }">
                        {{ fileName }}
                      </span>
                      <span v-if="index < fileInfo.fileCount - 1" class="text-gray-400 mr-1">,</span>
                    </template>
                  </template>
                  <span v-else class="font-semibold">
                    {{ fileInfo.fileTextDisplay }}
                  </span>
                  <span class="text-gray-500 ml-1 font-normal text-lg">({{ pages.length }} {{ fileInfo.pageText }})</span>
                </span>
              </p>
            <div class="flex-1"></div>

              <button @click="resetAll" class="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg flex gap-2 font-semibold transition shadow-sm">
                <RotateCcw :size="18" class="mt-1" />
                {{ t('restart') }}
              </button>
              <button @click="deleteSelected" class="bg-red-100 hover:bg-[rgb(249,216,216)] text-red-600 px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 shadow-sm" :disabled="selectedUids.length === 0">
                <Trash2 :size="20" />
                {{ t('delete') }}
              </button>
              <button
                @click="saveAndDownload"
                :disabled="isDownloading"
                class="relative bg-red-500 hover:bg-red-600 disabled:opacity-70 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md flex gap-2 font-semibold transition text-base shadow-md">
                <!-- Loader -->
                <span v-if="isDownloading" class="flex items-center gap-2">
                  <svg class="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 100 16v-4l-3.5 3.5L12 24v-4a8 8 0 01-8-8z">
                    </path>
                  </svg>
                  {{ t('processing_btn') }}
                </span>

                <!-- Normal Button -->
                <span v-else class="flex items-center gap-2">
                  <Download :size="20" class="mt-0.5" />
                  {{ t('download_pdf') }}
                </span>
              </button>
          </div>
        </div>
        <!-- Processing pages message -->
        <div v-if="isProcessing" class="bg-red-50 border border-red-200 rounded-md p-3 mb-2 flex items-center gap-3">
          <div class="animate-spin rounded-full h-5 w-5 border-2 border-red-400 border-t-red-600"></div>
          <p class="text-red-800 font-medium">{{ t('processing_pages', { count: totalPagesBeingProcessed }) }}... {{ t('appear_shortly') }}</p>
        </div>
        <div class="flex items-start gap-4">
          <div ref="scrollContainer" 
            class="flex-1 overflow-y-auto p-4 mb-5 border-2 border-transparent transition-all duration-300 rounded-lg"
            :class="{'border-red-400 bg-red-50/30 ring-4 ring-red-100 ring-inset': isDraggingMore}"
            :style="pages.length > 15 ? `max-height: ${maxHeight}px` : ''"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="isDraggingMore = false"
            @drop.prevent="handleDrop">
            <!-- Pages Grid + Add More Button in SAME ROW -->
               <div class="max-h-[360px] grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-6 gap-4">
                  <!-- Draggable Pages (takes available space) -->
                  <draggable 
                    v-model="pages" 
                    item-key="uid" 
                    class="contents"
                    ghost-class="sortable-ghost"
                    drag-class="sortable-drag"
                    :animation="300"
                  >
                    <template #item="{ element }">
                      <div class="relative group cursor-move" :style="{'--file-color': element.fileColor}">
                        <!-- Add blank page left -->
                        <button @click.stop="addBlankPage(pages.indexOf(element), 0, element.fileColor)" 
                          class="absolute left-[-10px] top-1/2 -translate-y-1/2 z-50 opacity-0 group-hover:opacity-100 transition-all duration-200 bg-white text-red-400 hover:text-red-500 rounded-md p-[4px] shadow-2xl border border-red-100 hover:bg-red-50"
                          title="Add a Blank Page Before">
                          <FilePlus :size="20" />
                        </button>

                        <!-- Add blank page right -->
                        <button @click.stop="addBlankPage(pages.indexOf(element), 1, element.fileColor)" 
                          class="absolute right-[-10px] top-1/2 -translate-y-1/2 z-50 opacity-0 group-hover:opacity-100 transition-all duration-200 bg-white text-red-400 hover:text-red-500 rounded-md p-[4px] shadow-2xl border border-red-100 hover:bg-red-50"
                          title="Add a Blank Page After">
                          <FilePlus :size="20" />
                        </button>

                        <div class="relative bg-white rounded-xl overflow-hidden transition-all shadow-sm hover:shadow-md border-2"
                          :class="[
                            selectedUids.includes(element.uid) ? 'border-red-500 ring-2 ring-red-500/20' : 'hover:border-red-200'
                          ]" 
                          :style="!selectedUids.includes(element.uid) ? { borderColor: element.fileColor, boxShadow: `0 4px 6px -1px ${element.fileColor}20, 0 2px 4px -1px ${element.fileColor}10` } : { boxShadow: '0 10px 15px -3px rgba(239, 68, 68, 0.2)' }"
                          @click.stop="toggleSelect(element.uid)">
                        <!-- Loading overlay -->
                        <div v-if="element.isLoading" class="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center z-20 rounded-lg">
                          <div class="flex flex-col items-center gap-2">
                            <div class="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-red-500"></div>
                            <p class="text-white text-xs font-semibold">Loading...</p>
                          </div>
                        </div>

                        <!-- Thumbnail -->
                        <div class="aspect-square overflow-hidden bg-gray-100 flex items-center justify-center shadow-md">
                          <img 
                            :src="element.thumbnail" 
                            class="max-w-[90%] max-h-[90%] p-0 object-contain"
                            :style="{ transform: `rotate(${element.rotation}deg)` }"
                          />
                        </div>

                        <!-- Page number -->
                        <div class="absolute bottom-2 right-2 bg-black/50 backdrop-blur-md text-white px-2 py-0.5 rounded-md text-[10px] font-bold shadow-sm z-10 flex items-center gap-1">
                          {{ element.isBlank ? 'Blank' : element.pageNum }}
                        </div>

                        <!-- Rotation badge -->
                        <div v-if="element.rotation !== 0" class="absolute top-2 left-2 bg-gray-600 text-white px-2 py-0.5 rounded text-xs font-semibold shadow-md">
                          {{ element.rotation }}°
                        </div>

                        <!-- Selection indicator -->
                        <div v-if="selectedUids.includes(element.uid)" class="absolute inset-0 bg-red-100 bg-opacity-20 border-2 border-red-500 rounded-lg flex items-center justify-center pointer-events-none">
                          <div class="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-lg shadow-lg">
                            ✓
                          </div>
                        </div>

                        <!-- Hover actions -->
                        <div class="absolute inset-0 bg-black/20 transition-all rounded-lg opacity-0 group-hover:opacity-100 z-30">
                          <!-- Top left: Rotation actions -->
                          <div class="absolute top-2 left-2 flex gap-1.5">
                            <button @click.stop="rotateSinglePage(element, -90)" class="p-[5px] bg-black/40 hover:bg-black/60 rounded-md text-white transition backdrop-blur-md shadow-sm" title="Rotate Left">
                              <RotateCcw :size="13" />
                            </button>
                            <button @click.stop="rotateSinglePage(element, 90)" class="p-[5px] bg-black/40 hover:bg-black/60 rounded-md text-white transition shadow-sm" title="Rotate Right">
                              <RotateCw :size="13" />
                            </button>
                            <button @click.stop="duplicatePage(element)" class="p-[5px] bg-black/40 hover:bg-black/60 rounded-md text-white transition shadow-sm" title="Duplicate Page">
                              <CopyCheck :size="14" />
                            </button>
                          </div>
                          
                          <!-- Top right: Delete action -->
                          <div class="absolute top-2 right-2">
                            <button @click.stop="deleteSinglePage(element.uid)" class="p-[5px] bg-red-500/90 hover:bg-red-600 rounded-md text-white transition backdrop-blur-md shadow-sm" title="Delete Page">
                              <Trash2 :size="14" />
                            </button>
                          </div>

                          <!-- Center: Selection Hint -->
                          <div class="absolute inset-0 flex items-end mb-2 justify-center pointer-events-none">
                            <p class="text-[8px] mb-0.5 font-bold text-white uppercase tracking-widest bg-black/50 px-2 py-0.5 rounded-full backdrop-blur-sm border border-white/10">
                              {{ t('click_to_select') }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </draggable>
                  <!-- Add More PDFs Button -->
                  <div v-if="pages.length > 0" @click="addMoreFiles" class="relative bg-gray-100 shadow-md rounded-xl border-2 cursor-pointer hover:border-red-500 hover:bg-gray-100 transition-all">
                    <!-- Main content -->
                    <div class="aspect-square flex flex-col items-center justify-center p-2 relative z-10">
                      
                      <!-- Upload icon with red/white/gray scheme -->
                      <div class="w-14 h-14 bg-red-100 text-red-600 font-bold rounded-md flex items-center justify-center mb-2">
                        <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                        </svg>
                      </div>
                      <!-- Text -->
                      <p class="text-sm font-semibold text-gray-800 text-center">+ {{ t('add_more') }}</p>
                      <p class="text-xs font-medium text-gray-500 mt-1">{{ t('files') }}</p>
                    </div>
                  </div>
              </div>
          </div>

          <!-- Floating Sorting Sidebar -->
          <div class="hidden md:flex flex-col gap-3 py-4 pr-4 sticky top-0 self-start">
            <button @click="addMoreFiles" class="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-600 transition-all group relative" title="Add More Files">
              <Plus :size="24" />
              <span class="absolute right-full mr-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">Add Files</span>
            </button>

            <div class="w-12 h-[1px] bg-gray-200 my-1"></div>

            <!-- Alphabetical Sort -->
            <button @click="sortFilesByName" 
              class="w-12 h-12 bg-white border-2 border-gray-100 rounded-full flex flex-col items-center justify-center shadow-sm hover:border-red-500 hover:text-red-500 transition-all relative group"
              :class="isSortingActive ? 'text-red-500' : 'text-gray-600'">
              <div class="flex flex-col items-center leading-none">
                <ArrowDownAz v-if="nameSortDir === 'asc'" :size="20" />
                <ArrowUpAz v-else :size="20" />
              </div>
              <span class="absolute right-full mr-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">Sort by Name</span>
            </button>

            <!-- Numeric Sort -->
            <button @click="sortPagesByNumber" 
              class="w-12 h-12 bg-white border-2 border-gray-100 rounded-full flex flex-col items-center justify-center shadow-sm hover:border-red-500 hover:text-red-500 transition-all relative group"
              :class="isSortingActive ? 'text-red-500' : 'text-gray-600'">
              <div class="flex flex-col items-center leading-none">
                <ArrowDown01 v-if="numberSortDir === 'asc'" :size="20" />
                <ArrowUp01 v-else :size="20" />
              </div>
              <span class="absolute right-full mr-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">Sort by Page #</span>
            </button>

            <!-- Reset Sort -->
            <button @click="resetSort" 
              class="w-12 h-12 bg-white/80 backdrop-blur-md border border-white/20 text-gray-600 rounded-full flex flex-col items-center justify-center shadow-lg hover:border-red-400 hover:text-red-500 transition-all group active:scale-95">
              <div class="flex flex-col items-center leading-none">
                <span class="text-[9px] font-bold">1-2</span>
                <div class="w-4 h-[1px] bg-gray-300 my-0.5"></div>
                <span class="text-[9px] font-bold">1-2</span>
              </div>
              <span class="absolute right-full mr-2 px-2 py-1 bg-gray-800/90 backdrop-blur-sm text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-all whitespace-nowrap pointer-events-none transform translate-x-2 group-hover:translate-x-0">Original Order</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Hidden file input -->
    <input
      type="file"
      ref="fileInput"
      multiple
      accept="application/pdf"
      class="hidden"
      @change="onFileChange"
    />
  </div>
  <!-- Footer -->
  <transition name="slide-up">
    <div v-if="selectedUids.length > 0" class="fixed bottom-0 left-0 right-0 flex items-center justify-between bg-white p-3 rounded-t-xl shadow-2xl border-t-2 border-gray-300 max-w-4xl mx-auto z-40">
      <!-- Left Section -->
      <div class="flex items-center gap-2">
        <div class="w-7 h-7 bg-red-600 text-white px-2 py-1.5 rounded-full text-lg font-semibold shadow-md flex items-center justify-center">
          {{ selectedUids.length }}
        </div>
        <span class="text-gray-800 text-lg font-semibold">
          {{ selectedUids.length === 1 ? t('page_selected') : t('pages_selected') }}
        </span>
        <!-- Select All Button -->
        <button @click="selectAll" class="px-4 py-1.5 rounded-full ml-2 border border-red-600 text-red-700 font-semibold hover:bg-red-100 transition"
          :disabled="selectedUids.length === pages.length">
          <span class="mr-1 ">✓</span>  {{ t('select_all') }}
        </button>
        <!-- Clear All Button -->
        <button @click="deselectAll" class="px-4 py-1.5 rounded-full ml-2 border border-red-600 text-red-700 font-semibold hover:bg-red-100 transition"
          :disabled="selectedUids.length === 0">
          <span class="mr-1 ">X</span>  {{ t('cancel_all') }}
        </button>
      </div>
      <!-- Right Section: Icons -->
      <div class="flex items-center gap-2"> 
        <!-- Preview -->
        <button
          @click="previewSelected"
          class="flex items-center justify-center bg-red-100 hover:bg-[rgb(249,216,216)] text-red-600 px-4 py-3 shadow-sm rounded-lg font-medium transition disabled:opacity-40"
          :disabled="selectedUids.length === 0">
          <Eye :size="20" class="text-red-600" />
        </button>
        <!-- Rotate -->
        <button
          @click="rotateSelected(-90)"
          class="flex items-center justify-center bg-red-100 hover:bg-[rgb(249,216,216)] text-red-600 px-4 py-3 shadow-sm rounded-lg font-medium transition disabled:opacity-40"
          :disabled="selectedUids.length === 0">
          <RotateCcw :size="20" class="text-red-600" />
        </button>
        <!-- Rotate -->
        <button
          @click="rotateSelected(90)"
          class="flex items-center justify-center bg-red-100 hover:bg-[rgb(249,216,216)] text-red-600 px-4 py-3 shadow-sm rounded-lg font-medium transition disabled:opacity-40"
          :disabled="selectedUids.length === 0">
          <RotateCw :size="20" class="text-red-600" />
        </button>
        <!-- Delete Button -->
        <button @click="deleteSelected"
          class="w-11 h-11 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-xl transition disabled:opacity-40"
          :disabled="selectedUids.length === 0">
          <Trash2 :size="20" />
        </button>
      </div>
    </div>
  </transition>
  <!-- Fullscreen Preview Modal -->
  <div v-if="showPreviewModal" class="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-50">
    <div class="w-full h-full flex flex-col overflow-hidden max-w-full max-h-full">

      <!-- Top Toolbar -->
      <div class="bg-[#2a2a2a] px-6 py-2 flex items-center justify-between shadow-lg">
        <!-- Left Section: Page Actions -->
        <div class="flex items-center gap-1.5">
          <button @click="rotateCurrentPage(-90)" class="text-white p-2 hover:bg-white/10 rounded-full transition-all active:scale-90" title="Rotate Left">
            <RotateCcw :size="20" />
          </button>
          <button @click="rotateCurrentPage(90)" class="text-white p-2 hover:bg-white/10 rounded-full transition-all active:scale-90" title="Rotate Right">
            <RotateCw :size="20" />
          </button>
          <button @click="deleteCurrentPage" class="text-white p-2 hover:bg-red-500/20 hover:text-red-400 rounded-full transition-all ml-2 active:scale-90" title="Delete Page">
            <Trash2 :size="20" />
          </button>
        </div>

        <!-- Center Section: Page Navigation (As requested in image) -->
        <div class="flex items-center gap-6">
          <button @click="swiperInstance?.slidePrev()" 
            class="text-white/70 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            :disabled="swiperActiveIndex === 0">
            <ChevronLeft :size="28" />
          </button>
          
          <div class="flex items-center gap-2 text-white font-semibold select-none">
             <span class="bg-white/10 px-3 py-0.5 rounded text-xl min-w-[40px] text-center">{{ swiperActiveIndex + 1 }}</span>
             <span class="text-white/40 text-xl mx-0.5">/</span>
             <span class="text-xl">{{ previewPages.length }}</span>
          </div>

          <button @click="swiperInstance?.slideNext()" 
            class="text-white/70 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            :disabled="swiperActiveIndex === previewPages.length - 1">
            <ChevronRight :size="28" />
          </button>
        </div>

        <!-- Right Section: Close -->
        <div class="flex items-center">
          <button @click="showPreviewModal = false" class="text-white/80 hover:text-white hover:bg-red-500 p-2 rounded-lg transition-all active:scale-95">
            <X :size="28" />
          </button>
        </div>
      </div>

      <!-- Swiper Preview -->
      <div class="flex-1 flex items-center justify-center overflow-hidden bg-black/20 backdrop-blur-md">
            <Swiper
              :key="swiperRerenderKey"
              :modules="[Navigation, Pagination]"
              navigation
              pagination
              space-between="20"
              slides-per-view="1"
              class="w-full h-full"
              @swiper="onSwiper"
              @slideChange="onSlideChange"
              ref="swiperRef">
                    <SwiperSlide
              v-for="(page, index) in previewPages"
              :key="page.uid + '-' + page.rotation"
            >
            <div class="flex flex-col items-center justify-center w-full h-full p-4 md:p-8">
              <img
                :key="page.uid + '-' + page.rotation + '-' + (page.thumbnail ? page.thumbnail.substring(0,50) : '')"
                :src="page.thumbnail"
                :alt="`Page ${page.pageNum}`"
                class="max-w-full max-h-full object-contain shadow-2xl rounded-sm transition-transform duration-300"
                :style="{ transform: `rotate(${page.rotation}deg)` }"
              />
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import draggable from 'vuedraggable'
import { PDFDocument, degrees } from 'pdf-lib'
import * as pdfjsLib from 'pdfjs-dist/build/pdf'
import { Upload, RotateCcw, RotateCw, Trash2, Download, X, Plus, FilePlus, ArrowDownAz, ArrowUpAz, ArrowDown01, ArrowUp01, ListRestart, Eye, ChevronLeft, ChevronRight, CopyCheck } from 'lucide-vue-next'

// Props for localization
const props = defineProps({
  translations: {
    type: Object,
    required: true
  }
})

// Translation helper
const t = (key, replacements = {}) => {
  let translation = props.translations[key] || key
  for (const [k, v] of Object.entries(replacements)) {
    translation = translation.replace(`:${k}`, v)
  }
  return translation
}

// Set worker for pdfjs
pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.js';

// Swiper Correct Imports (2025 ke liye)
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Pagination, Zoom } from 'swiper/modules'   // YEH WAALA
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/zoom'

const swiperRef = ref(null)
const swiperInstance = ref(null)
const swiperRerenderKey = ref(0)
const swiperActiveIndex = ref(0)
const isDownloading = ref(false)

// Sorting State
const nameSortDir = ref('asc')
const numberSortDir = ref('asc')
const isSortingActive = ref(false)

const applyCombinedSort = () => {
  isSortingActive.value = true
  
  // 1. Blank pages ki positions save kro aur unhe sort se bahar nikal lo
  const blanks = pages.value.map((p, i) => p.isBlank ? {p, i} : null).filter(x => x !== null);
  const nonBlanks = pages.value.filter(p => !p.isBlank);

  // 2. Sirf non-blanks ko sort kro
  nonBlanks.sort((a, b) => {
    // Pehle File Name se sort kro (using nameSortDir)
    const nameResult = a.fileName.localeCompare(b.fileName, undefined, { numeric: true, sensitivity: 'base' })
    if (nameResult !== 0) {
      return nameSortDir.value === 'asc' ? nameResult : -nameResult
    }
    
    // Agar file same hai, to Page Number se sort kro (using numberSortDir)
    const pageResult = a.pageNum - b.pageNum
    return numberSortDir.value === 'asc' ? pageResult : -pageResult
  })

  // 3. Sorted pages mein blanks ko wapas unki original positions pr daal do
  const sorted = [...nonBlanks];
  blanks.forEach(b => sorted.splice(b.i, 0, b.p));
  pages.value = sorted;
}

const sortFilesByName = () => {
  nameSortDir.value = nameSortDir.value === 'asc' ? 'desc' : 'asc'
  applyCombinedSort()
}

const sortPagesByNumber = () => {
  numberSortDir.value = numberSortDir.value === 'asc' ? 'desc' : 'asc'
  applyCombinedSort()
}

const resetSort = () => {
  pages.value.sort((a, b) => a.uid - b.uid)
  isSortingActive.value = false
  nameSortDir.value = 'asc'
  numberSortDir.value = 'asc'
}

const previewSelected = () => {
  previewPages.value = pages.value.filter(p => selectedUids.value.includes(p.uid))
  swiperActiveIndex.value = 0
  showPreviewModal.value = true
  nextTick(() => {
    swiperRef.value?.swiper?.slideTo(0)
  })
}

const onSwiper = (swiper) => {
  swiperInstance.value = swiper
}

const onSlideChange = (swiper) => {
  swiperActiveIndex.value = swiper.activeIndex
}

const rotateCurrentPage = async (deg) => {
  const page = previewPages.value[swiperActiveIndex.value];
  if (!page) return;

  // 1. Update rotation immediately
  page.rotation = (page.rotation + deg + 360) % 360;

  // 2. Render new HD thumbnail (background)
  const pdfjsDoc = pdfjsDocs.value[page.fileName];
  if (pdfjsDoc) {
    page.isLoading = true;
    try {
      const { thumbnail } = await renderPage(pdfjsDoc, page.pageNum, page.rotation);
      page.thumbnail = thumbnail;
    } finally {
      page.isLoading = false;
    }
  }

  // 3. Force re-render of current slide UI
  swiperRerenderKey.value++;

  await nextTick();
  if (swiperRef.value?.swiper) {
    swiperRef.value.swiper.slideTo(swiperActiveIndex.value, 0);
  }
};

const deleteCurrentPage = () => {
  const page = previewPages.value[swiperActiveIndex.value]
  if (!page) return

  // Remove from main pages array
  pages.value = pages.value.filter(p => p.uid !== page.uid)
  selectedUids.value = selectedUids.value.filter(uid => uid !== page.uid)
  previewPages.value = previewPages.value.filter(p => p.uid !== page.uid)

  if (previewPages.value.length === 0) {
    showPreviewModal.value = false
  } else if (swiperActiveIndex.value >= previewPages.value.length) {
    swiperActiveIndex.value = previewPages.value.length - 1
    nextTick(() => {
      swiperRef.value?.swiper?.slideTo(swiperActiveIndex.value)
    })
  }
}


// Refs
const pages = ref([])
const scrollContainer = ref(null)
const selectedUids = ref([])
const pdfjsDocs = ref({})
const pdfLibDocs = ref({})
const previewPages = ref([])
const showPreviewModal = ref(false)
const previewCanvases = ref({})
const isDragging = ref(false)
const isDraggingMore = ref(false)
const isProcessing = ref(false)
const totalPagesBeingProcessed = ref(0)
let uidCounter = 0
const fileInput = ref(null)
const currentPreviewIndex = ref(0)
const previewCanvas = ref(null)

// File Color Logic - Generates unique light pastel colors using HSL
const fileColors = ref({})
let colorIndex = 0

const getNextFileColor = () => {
  // Use HSL with fixed high lightness (65-75%) and saturation (60-70%) 
  // to ensure "pastel" light look while maintaining distinct hues.
  const hue = (colorIndex * 137.5) % 360; // Golden angle for unique distribution
  colorIndex++;
  return `hsl(${hue}, 65%, 70%)`;
};

const fileInfo = computed(() => {
  const uniqueFiles = [...new Set(pages.value.map(p => p.fileName))]
  const fileCount = uniqueFiles.length

  // Page plural
  const pageText = pages.value.length === 1 ? t('page') : t('pages')

  // File text logic:
  let fileTextDisplay = ''

  if (fileCount <= 2) {
    // 1 ya 2 files → names show
    fileTextDisplay = uniqueFiles.join(', ')
  } else {
    // 3+ files → sirf count show
    fileTextDisplay = `${fileCount} ${t('files')}`
  }

  return { uniqueFiles, fileCount, fileTextDisplay, pageText }
})

// Dropzone options
const dropzoneOptions = {
  url: 'https://httpbin.org/post',
  autoProcessQueue: false,
  acceptedFiles: 'application/pdf',
  addRemoveLinks: false,
}

const handleDrop = (e) => {
  isDragging.value = false
  isDraggingMore.value = false
  const files = Array.from(e.dataTransfer.files)
  if (files.length > 0) {
    processFiles(files)
  }
}

const onDragOver = (e) => {
  if (e.dataTransfer.types && Array.from(e.dataTransfer.types).includes('Files')) {
    isDraggingMore.value = true
  }
}

const handlePaste = (e) => {
  const items = e.clipboardData?.items
  if (!items) return
  
  for (let item of items) {
    if (item.type === 'application/pdf' || item.type.startsWith('application/')) {
      const file = item.getAsFile()
      if (file && file.type === 'application/pdf') {
        processFiles([file])
        break
      }
    }
  }
}

const processFiles = async (files) => {
  const pdfFiles = files.filter(f => f.type === 'application/pdf')
  if (!pdfFiles.length) {
    alert('Please select PDF files only')
    return
  }

  isProcessing.value = true

  for (const file of pdfFiles) {
    try {
      const buffer = await file.arrayBuffer()

      // Assign color if new file
      if (!fileColors.value[file.name]) {
        fileColors.value[file.name] = getNextFileColor()
      }

      // Load with pdf-lib
      const pdfLibDoc = await PDFDocument.load(buffer)
      pdfLibDocs.value[file.name] = pdfLibDoc

      // Load with pdfjs
      const pdfjsDoc = await pdfjsLib.getDocument({ data: buffer }).promise
      pdfjsDocs.value[file.name] = pdfjsDoc

      totalPagesBeingProcessed.value = pdfjsDoc.numPages

      // Generate thumbnails with loading state
      for (let i = 1; i <= pdfjsDoc.numPages; i++) {
        pages.value.push({
          uid: uidCounter++,
          fileName: file.name,
          pageNum: i,
          rotation: 0,
          thumbnail: '',
          isLoading: true,
          fileColor: fileColors.value[file.name]
        })
      }

      // Auto-scroll to bottom after adding new pages
      scrollToBottom()

      // Render pages sequentially
      let pageIndex = pages.value.length - pdfjsDoc.numPages
      for (let i = 1; i <= pdfjsDoc.numPages; i++) {
        const { thumbnail } = await renderPage(pdfjsDoc, i, 0)
        if (pages.value[pageIndex]) {
          pages.value[pageIndex].thumbnail = thumbnail
          pages.value[pageIndex].isLoading = false
        }
        pageIndex++
      }
    } catch (error) {
      console.error('Error processing PDF:', error)
      alert('Error processing PDF file. Please try again.')
    }
  }

  isProcessing.value = false
  totalPagesBeingProcessed.value = 0
  
  // By default sort A->Z and 0->1 after upload
  if (pages.value.length > 0) {
    nameSortDir.value = 'asc'
    numberSortDir.value = 'asc'
    applyCombinedSort()
  }
}

const scrollToBottom = () => {
  nextTick(() => {
    if (scrollContainer.value) {
      scrollContainer.value.scrollTo({
        top: scrollContainer.value.scrollHeight,
        behavior: 'smooth'
      })
    }
  })
}

const rotateSinglePage = async (page, deg) => {
  if (page.isBlank) {
    page.rotation = (page.rotation + deg + 360) % 360
    return
  }
  try {
    page.isLoading = true
    const pdfjsDoc = pdfjsDocs.value[page.fileName]
    if (!pdfjsDoc) return

    page.rotation = (page.rotation + deg + 360) % 360
    const { thumbnail } = await renderPage(pdfjsDoc, page.pageNum, page.rotation)
    page.thumbnail = thumbnail
  } catch (error) {
    console.error('Error rotating page:', error)
  } finally {
    page.isLoading = false
  }
}

const deleteSinglePage = (uid) => {
  pages.value = pages.value.filter(p => p.uid !== uid)
  selectedUids.value = selectedUids.value.filter(id => id !== uid)
}

const duplicatePage = (page) => {
  const index = pages.value.findIndex(p => p.uid === page.uid)
  if (index === -1) return

  // Create a copy with a new unique ID
  const newPage = {
    ...JSON.parse(JSON.stringify(page)), // Deep copy to avoid reference issues
    uid: uidCounter++,
    isLoading: false
  }

  // Insert right after the current page
  pages.value.splice(index + 1, 0, newPage)
}

const addBlankPage = (index, offset, color) => {
  const blankPage = {
    uid: uidCounter++,
    fileName: 'blank',
    pageNum: 0,
    rotation: 0,
    thumbnail: createBlankThumbnail(),
    isLoading: false,
    isBlank: true,
    fileColor: color || '#e5e7eb' // Use neighbor's color or default gray
  }
  pages.value.splice(index + offset, 0, blankPage)
}

const createBlankThumbnail = () => {
  const canvas = document.createElement('canvas')
  canvas.width = 400
  canvas.height = 560
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = 'white'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  return canvas.toDataURL()
}

const onFileChange = (e) => {
  const files = Array.from(e.target.files)
  if (files.length) processFiles(files)
  e.target.value = ''
}

const addMoreFiles = () => {
  fileInput.value.click()
}

const renderPage = async (pdfDoc, pageNum, rotation = 0) => {
  const page = await pdfDoc.getPage(pageNum);

  const viewport = page.getViewport({
    scale: 2.5,
    rotation,
  });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({ canvasContext: ctx, viewport }).promise;

  return { thumbnail: canvas.toDataURL("image/png") };
};


const toggleSelect = (uid) => {
  const idx = selectedUids.value.indexOf(uid)
  if (idx === -1) selectedUids.value.push(uid)
  else selectedUids.value.splice(idx, 1)
}

// Select and deselect all functionality
const selectAll = () => {
  selectedUids.value = pages.value.map(p => p.uid)
}

const deselectAll = () => {
  selectedUids.value = []
}

const rotateSelected = async (deg) => {
  try {
    // Set loading state for ALL selected pages first
    const selectedPages = pages.value.filter(p => selectedUids.value.includes(p.uid))
    selectedPages.forEach(p => {
      p.isLoading = true
    })

    // Render pages for ALL selected items (parallel processing)
    const renderPromises = selectedPages.map(async (page) => {
      try {
        const pdfjsDoc = pdfjsDocs.value[page.fileName]
        if (!pdfjsDoc) {
          console.error('[v0] PDF document not found for file:', page.fileName)
          return
        }

        // Update rotation
        page.rotation = (page.rotation + deg + 360) % 360
        
        // Render with new rotation
        const { thumbnail } = await renderPage(pdfjsDoc, page.pageNum, page.rotation)
        page.thumbnail = thumbnail
      } catch (error) {
        console.error('[v0] Error rendering page:', error)
      } finally {
        // Ensure loading state is cleared ALWAYS
        page.isLoading = false
      }
    })

    // Wait for all renders to complete
    await Promise.all(renderPromises)
  } catch (error) {
    console.error('[v0] Error in rotateSelected:', error)
    // Clear loading states on error
    pages.value.forEach(p => {
      if (selectedUids.value.includes(p.uid)) {
        p.isLoading = false
      }
    })
  }
}

const deleteSelected = () => {
  pages.value = pages.value.filter(p => !selectedUids.value.includes(p.uid))
  selectedUids.value = []
}


const renderPreviewPage = async (index) => {
  if (!previewPages.value[index]) return

  await nextTick()

  const canvas = previewCanvas.value
  if (!canvas) {
  console.error('Preview canvas not found')
    return
  }

  const page = previewPages.value[index]
  const pdfjsDoc = pdfjsDocs.value[page.fileName]
  if (!pdfjsDoc) {
    console.error('PDF document not loaded for preview')
    return
  }

  try {
    const pdfPage = await pdfjsDoc.getPage(page.pageNum)

    // Get container size for responsive scaling
    const container = canvas.parentElement
    const maxWidth = container.clientWidth * 0.9
    const maxHeight = container.clientHeight * 0.9

    // Get original viewport
    let viewport = pdfPage.getViewport({ scale: 1, rotation: page.rotation })
    let scale = Math.min(maxWidth / viewport.width, maxHeight / viewport.height, 3) // Max scale 3
    scale = Math.max(scale, 0.5) // Min scale

    viewport = pdfPage.getViewport({ scale, rotation: page.rotation })

    // Set canvas size
    canvas.width = viewport.width
    canvas.height = viewport.height
    canvas.style.width = `${viewport.width}px`
    canvas.style.height = `${viewport.height}px`

    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Use requestAnimationFrame to ensure rendering after resize
    requestAnimationFrame(async () => {
      try {
        const renderContext = {
          canvasContext: ctx,
          viewport: viewport,
        }
        await pdfPage.render(renderContext).promise
      } catch (err) {
        console.error('Render failed:', err)
      }
    })
  } catch (error) {
    console.error('Error rendering preview:', error)
  }
}

const previewNext = async () => {
  if (currentPreviewIndex.value < previewPages.value.length - 1) {
    currentPreviewIndex.value++
    await renderPreviewPage(currentPreviewIndex.value)
  }
}

const previewPrevious = async () => {
  if (currentPreviewIndex.value > 0) {
    currentPreviewIndex.value--
    await renderPreviewPage(currentPreviewIndex.value)
  }
}

const rotatePreviewPage = async (deg) => {
  const page = previewPages.value[currentPreviewIndex.value]
  if (!page) return

  try {
    page.isLoading = true
    page.rotation = (page.rotation + deg + 360) % 360
    
    // Update thumbnail for the page in grid
    const pdfjsDoc = pdfjsDocs.value[page.fileName]
    const { thumbnail } = await renderPage(pdfjsDoc, page.pageNum, page.rotation)
    page.thumbnail = thumbnail

    // Re-render preview with new rotation
    await renderPreviewPage(currentPreviewIndex.value)
  } catch (error) {
    console.error('[v0] Error rotating preview page:', error)
  } finally {
    page.isLoading = false
  }
}

const deletePreviewPage = () => {
  const page = previewPages.value[currentPreviewIndex.value]
  if (!page) return

  // Remove from main pages array
  pages.value = pages.value.filter(p => p.uid !== page.uid)
  previewPages.value = previewPages.value.filter(p => p.uid !== page.uid)
  selectedUids.value = selectedUids.value.filter(uid => uid !== page.uid)

  // Update preview index
  if (currentPreviewIndex.value >= previewPages.value.length) {
    currentPreviewIndex.value = Math.max(0, previewPages.value.length - 1)
  }

  // Re-render or close if no pages left
  if (previewPages.value.length === 0) {
    showPreviewModal.value = false
  } else {
    renderPreviewPage(currentPreviewIndex.value)
  }
}

const createMergedPdf = async () => {
  const mergedPdf = await PDFDocument.create()
  for (const page of pages.value) {
    if (page.isBlank) {
      mergedPdf.addPage() // Default A4 blank page
      continue
    }
    const origPdf = pdfLibDocs.value[page.fileName]
    if (!origPdf) continue
    const [copiedPage] = await mergedPdf.copyPages(origPdf, [page.pageNum - 1])
    if (page.rotation !== 0) {
      copiedPage.setRotation(degrees(page.rotation))
    }
    mergedPdf.addPage(copiedPage)
  }
  return await mergedPdf.save()
}

const saveAndDownload = async () => {
  try {
    isDownloading.value = true   // Loader ON

    await nextTick()             // UI freeze na ho
    await new Promise(resolve => setTimeout(resolve, 50)) // Device hang se bachao

  const bytes = await createMergedPdf()
  const blob = new Blob([bytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `organized-${Date.now()}.pdf`
  a.click()
  URL.revokeObjectURL(url)
 await generateAndDownloadPDF()

  } catch (e) {
    console.error(e)
  } finally {
    isDownloading.value = false  // Loader OFF
  }
}

// Reset all pages
const resetAll = () => {
  pages.value = []
  selectedUids.value = []
  pdfjsDocs.value = {}
  pdfLibDocs.value = {}
  fileColors.value = {}
  colorIndex = 0
  uidCounter = 0
}

// Calculate maxHeight dynamically based on 15 items
const maxHeight = computed(() => {
  // Estimate item height including padding/margin
  const itemHeight = 228 // 8rem = 128px
  const gap = 16 // Tailwind gap-4 = 1rem = 16px

  // Determine number of columns based on Tailwind breakpoints
  const width = window.innerWidth
  let columns = 2
  if (width >= 640) columns = 3
  if (width >= 768) columns = 4
  if (width >= 1024) columns = 5
  if (width >= 1280) columns = 5

  const rowsFor15Items = Math.ceil(15 / columns)
  return rowsFor15Items * itemHeight + (rowsFor15Items - 1) * gap
})

onMounted(() => {
  window.addEventListener('paste', handlePaste)
})

onUnmounted(() => {
  window.removeEventListener('paste', handlePaste)
  document.body.style.overflow = '' // Cleanup
})

// Scroll lock for preview modal
watch(showPreviewModal, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})
</script>

<style scoped>
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.75s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave-from {
  transform: translateY(0);
  opacity: 1;
}
:deep(.swiper-button-next),
:deep(.swiper-button-prev) {
  color: red !important;
}

:deep(.swiper-pagination-bullet) {
  background: red !important;
  opacity: 0.4;
}

:deep(.swiper-pagination-bullet-active) {
  background: red !important;
  opacity: 1;
}
:deep(.sortable-ghost) {
  opacity: 1 !important;
}

:deep(.sortable-ghost) > div {
  background-color: color-mix(in srgb, var(--file-color), transparent 80%) !important;
  border: 2px dashed var(--file-color) !important;
  box-shadow: none !important;
}

:deep(.sortable-ghost) img, 
:deep(.sortable-ghost) .absolute {
  opacity: 0.3;
}

:deep(.sortable-drag) {
  opacity: 1 !important;
}

:deep(.sortable-drag) > div {
  background-color: color-mix(in srgb, var(--file-color), transparent 40%) !important;
  border-color: var(--file-color) !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1) !important;
  transform: rotate(3deg) scale(1.05);
}

:deep(.sortable-drag) .group-hover\:opacity-100 {
  display: none !important;
}
</style>
